import { Add, ChevronRight } from '@mui/icons-material';
import { alpha, Box, Grid, styled, Theme, Typography } from '@mui/material';
import GridContainer from 'components/GridContainer';
import Page from 'components/new/Page';
import api from 'lib/trpcClient';
import { useState } from 'react';
import NoResults, { NoResultsMode } from './NoResults';
import PageLoader from 'components/PageLoader';
import { useNotification } from 'hooks/ui/useNotification';
import {
  ADD_BANK_ACCOUNT_WITHOUT_FEED,
  PROCESS_BANK_ACCOUNT,
} from 'adminConstants/routes';
import AccountSettings from '../AccountSettings';
import { useHistory, useLocation } from 'react-router-dom';
import { Column, Row } from 'further-ui/components/Layout';
import CountrySelector from 'further-ui/components/CountrySelector';
import SearchField from 'further-ui/components/SearchField';
import { countries } from 'further-ui/labels';

const Image = styled('img')(({ theme }: { theme: Theme }) => ({
  maxWidth: '50px',
  maxHeight: '50px',
  borderRadius: theme.shape.borderRadius,
}));

const Filters = styled(Row)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.sm),
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: theme.shape.borderRadius,
}));

const Filter = styled(Box)({
  width: '100%',
});

const BankItem = styled(Row)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.xs),
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: theme.shape.borderRadius,
  height: '60px',
  cursor: 'pointer',

  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
  },
}));

const AddAccountWithoutFeedItem = styled(BankItem)(({ theme }) => ({
  padding: theme.spacing(
    theme.spacingSizes.sm,
    theme.spacingSizes.xs,
    theme.spacingSizes.sm,
    theme.spacingSizes.sm,
  ),
}));

const Icon = styled(ChevronRight)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const BankName = styled(Typography)(() => ({
  flexGrow: 1,
}));

const AddNewBankAccount: React.FC = () => {
  const { search } = useLocation();
  const history = useHistory();
  const { error } = useNotification();
  const [selectedCountry, setSelectedCountry] = useState<string>();
  const [searchQuery, setSearchQuery] = useState<string>();

  const urlSearchParams = new URLSearchParams(search);
  const addWithoutFeed = urlSearchParams.get('addWithoutFeed');

  const selectedCountryCode = countries.find(
    (country) => country.name === selectedCountry,
  )?.code;

  const { data, isLoading } = api.bankFeed.listBanks.useQuery(
    {
      country: selectedCountryCode ?? '',
    },
    {
      enabled: !!selectedCountryCode,
    },
  );

  const authoriseBank = api.bankFeed.authoriseBank.useMutation({
    onSuccess: (data) => {
      if (!data) {
        error('Failed to authorise bank.');
        return;
      }

      window.location.href = data.redirectUrl;
    },
  });

  const handleSelectBank = (bankId: string) => {
    authoriseBank.mutate({
      institutionId: bankId,
      redirectUrl: `${window.location.protocol}//${
        window.location.host
      }${PROCESS_BANK_ACCOUNT}?${urlSearchParams.toString()}`,
    });
  };

  const availableBanks = (data ?? []).filter(
    (bank) =>
      !searchQuery ||
      bank.name.toLowerCase().includes(searchQuery?.toLowerCase() ?? ''),
  );

  const handleAddAccountWithoutFeed = () => {
    history.push(ADD_BANK_ACCOUNT_WITHOUT_FEED);
  };

  if (addWithoutFeed) {
    return (
      <Page
        tabTitle="Bank Reconciliation"
        heading="Add new account"
        spacing="lg"
        headingIcon={Add}
      >
        <AccountSettings />
      </Page>
    );
  }

  return (
    <Page tabTitle="Bank Reconciliation" heading="Find account" spacing="lg">
      <Filters fullWidth spacing="sm" centered>
        <Filter>
          <CountrySelector
            fullWidth
            value={selectedCountry}
            onChange={setSelectedCountry}
            placeholder="Select country"
            showPopularFirst
          />
        </Filter>
        <Filter>
          <SearchField
            value={searchQuery ?? ''}
            autoSearchOnDebounce
            onSearch={setSearchQuery}
            placeholder="Search"
            fullWidth
            allowEmptyQuery
            iconPlacement="left"
          />
        </Filter>
      </Filters>
      {isLoading || authoriseBank.isPending ? (
        <PageLoader />
      ) : !availableBanks.length ? (
        <NoResults
          mode={
            selectedCountry
              ? NoResultsMode.NoResults
              : NoResultsMode.NoCountrySelected
          }
        />
      ) : (
        <GridContainer>
          {availableBanks.length ? (
            <Grid size={12}>
              <AddAccountWithoutFeedItem
                centered
                spacing="sm"
                onClick={handleAddAccountWithoutFeed}
              >
                <Column fullWidth>
                  <BankName variant="h6">
                    Bank not listed? Click here to add an account manually
                  </BankName>
                  <Typography variant="body2">
                    You may want to do this if your bank doesn't currently
                    provide a bank feed for your account or you need a petty
                    cash account.
                  </Typography>
                </Column>
                <Icon />
              </AddAccountWithoutFeedItem>
            </Grid>
          ) : null}
          {availableBanks.map((bank) => (
            <Grid
              key={bank.id}
              size={{ xs: 12, md: 6 }}
              onClick={() => handleSelectBank(bank.id)}
            >
              <BankItem centered spacing="sm">
                <Image src={bank.logo} alt={bank.name} />
                <BankName variant="h6">{bank.name}</BankName>
                <Icon />
              </BankItem>
            </Grid>
          ))}
        </GridContainer>
      )}
    </Page>
  );
};

export default AddNewBankAccount;
